import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Layout } from "./components/Layout";
import { Dashboard } from "./pages/Dashboard";
import { QBOSetup } from "./pages/QBOSetup";
import { QBOCallback } from "./pages/QBOCallback";
import { AircallIntegration } from "./pages/AircallIntegration";
import { CompanyInfo } from "./pages/CompanyInfo";
import { BillingJobs } from "./pages/BillingJobs";
import { CustomerProfiles } from "./pages/CustomerProfiles";
import { Settings } from "./pages/Settings";
import { RegionTeamManagement } from "./pages/RegionTeamManagement";
import { AccessControl } from "./pages/AccessControl";
import { Budgeting } from "./pages/Budgeting";
import { BudgetingForecasting } from "./pages/BudgetingForecasting";
import { CaptureRates } from "./pages/CaptureRates";
import { PCCPerformance } from "./pages/PCCPerformance";
import { MinutesUsage } from "./pages/MinutesUsage";
import { MinutesUsageWebhooks } from "./pages/MinutesUsageWebhooks";
import { UnassignedQueues } from "./pages/UnassignedQueues";
import { CashFlow } from "./pages/CashFlow";
import { BillableOverages } from "./pages/BillableOverages";
import { TeamMembers } from "./pages/TeamMembers";
import Transcripts from "./pages/Transcripts";
import FailedTranscripts from "./pages/FailedTranscripts";
import HiringNeeds from "./pages/HiringNeeds";
import { ClockPortal } from "./pages/ClockPortal";
import { ClockManager } from "./pages/ClockManager";
import { TimesheetReviewTeamLead } from "./pages/TimesheetReviewTeamLead";
import { TimesheetReviewOm } from "./pages/TimesheetReviewOm";
import { PCCSchedule } from "./pages/PCCSchedule";
import { PayrollPage } from "./pages/PayrollPage";
import { Login } from "./pages/Login";
import { AcceptInvite } from "./pages/AcceptInvite";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { useAuthStore } from "./stores/authStore";
import { ACTION_KEYS } from "./constants/actions";
import { Toaster } from "react-hot-toast";

// An unauthenticated visit to a protected URL (e.g. a timesheet review email link opened while
// logged out) used to fall through to a bare <Navigate to="/login" /> with no memory of where
// the user was headed, dropping the path and query string. Stash the intended destination so
// Login can send them back to it after a successful sign-in.
function RedirectToLogin() {
  const location = useLocation();
  useEffect(() => {
    const dest = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem("postLoginRedirect", dest);
    } catch {
      // Storage unavailable (private mode, blocked site data) — falls back to the default
      // post-login landing page instead of the original destination.
    }
  }, [location]);
  return <Navigate to="/login" replace />;
}

function App() {
  const {
    isAuthenticated,
    hasHydrated,
    canManageAccess,
    canManageSettings,
    canViewBilling,
    canViewCaptureRate,
    canViewBudgeting,
    canViewCashFlow,
    canViewForecasting,
    canViewCustomerProfiles,
    canViewTelephony,
    canManageTeamMembers,
    canViewTranscripts,
    canViewPCCPerformance,
    canViewHiringNeeds,
    canClockSelf,
    canClockManage,
    canViewPayroll,
    canManageSchedule,
    canTimesheetTlReview,
    canTimesheetOmReview,
  } = useAuthStore(
    (state) => ({
      isAuthenticated: state.isAuthenticated,
      hasHydrated: state.hasHydrated,
      canManageAccess: state.hasPermission(ACTION_KEYS.ACCESS_MANAGE),
      canManageSettings: state.hasPermission(ACTION_KEYS.SETTINGS_MANAGE),
      canViewBilling: state.hasPermission(ACTION_KEYS.BILLING_VIEW),
      canViewCaptureRate: state.hasPermission(ACTION_KEYS.CAPTURE_RATE_VIEW),
      canViewBudgeting: state.hasPermission(ACTION_KEYS.BUDGETING_VIEW),
      canViewCashFlow: state.hasPermission(ACTION_KEYS.CASH_FLOW_VIEW),
      canViewForecasting: state.hasPermission(ACTION_KEYS.FORECASTING_VIEW),
      canViewCustomerProfiles:
        state.hasPermission(ACTION_KEYS.CUSTOMER_VIEW),
      canViewTelephony:
        state.hasPermission(ACTION_KEYS.INTEGRATION_GENESYS) ||
        state.hasPermission(ACTION_KEYS.BILLING_VIEW),
      canManageTeamMembers: state.hasPermission(ACTION_KEYS.TEAM_MEMBER_MANAGE) || state.hasPermission(ACTION_KEYS.TEAM_MEMBER_PAY),
      canViewTranscripts: state.hasPermission(ACTION_KEYS.TRANSCRIPT_VIEW),
      canViewPCCPerformance: state.hasPermission(ACTION_KEYS.PCC_PERFORMANCE_VIEW),
      canViewHiringNeeds: state.hasPermission(ACTION_KEYS.HIRING_NEEDS_VIEW),
      canClockSelf: state.hasPermission(ACTION_KEYS.CLOCK_SELF),
      canClockManage: state.hasPermission(ACTION_KEYS.CLOCK_MANAGE),
      canViewPayroll: state.hasPermission(ACTION_KEYS.PAYROLL_VIEW),
      canManageSchedule: state.hasPermission(ACTION_KEYS.SCHEDULE_MANAGE),
      canTimesheetTlReview: state.hasPermission(ACTION_KEYS.TIMESHEET_TL_REVIEW),
      canTimesheetOmReview: state.hasPermission(ACTION_KEYS.TIMESHEET_OM_REVIEW),
    })
  );
  const hydrate = useAuthStore((state) => state.hydrate);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    let lastHydrated = Date.now();
    const onFocus = () => {
      // Only re-check session if it's been more than 5 minutes since the last check
      if (Date.now() - lastHydrated > 5 * 60 * 1000) {
        lastHydrated = Date.now();
        void hydrate();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [hydrate]);

  if (!hasHydrated) {
    return (
      <>
        <Toaster position="top-right" />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-gray-600">Checking session...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/qbo-callback" element={<QBOCallback />} />

        {isAuthenticated ? (
          <Route path="/" element={<Layout />}>
            <Route index element={canClockSelf && !canViewBilling && !canManageAccess ? <Navigate to="/clock" replace /> : <Dashboard />} />
            <Route path="qbo-setup" element={<QBOSetup />} />
            <Route path="company-info" element={<CompanyInfo />} />
            {canViewTelephony && (
              <Route path="aircall" element={<AircallIntegration />} />
            )}
            {canViewBilling && (
              <Route path="billing-jobs" element={<BillingJobs />} />
            )}
            {canViewBudgeting && (
              <Route path="budgeting" element={<Budgeting />} />
            )}
            {canViewForecasting && (
              <Route
                path="budgeting-forecasting"
                element={<BudgetingForecasting />}
              />
            )}
            {(canViewCaptureRate || canViewBilling) && (
              <Route path="capture-rates" element={<CaptureRates />} />
            )}
            {canViewPCCPerformance && (
              <Route path="pcc-performance" element={<PCCPerformance />} />
            )}
            {canViewHiringNeeds && (
              <Route path="hiring-needs" element={<HiringNeeds />} />
            )}
            {canViewBilling && (
              <Route path="minutes-usage" element={<MinutesUsage />} />
            )}
            {canViewBilling && (
              <Route
                path="minutes-usage-webhooks"
                element={<MinutesUsageWebhooks />}
              />
            )}
            {canViewBilling && (
              <Route
                path="unassigned-queues"
                element={<UnassignedQueues />}
              />
            )}
            {canViewCustomerProfiles && (
              <Route
                path="customer-profiles"
                element={<CustomerProfiles />}
              />
            )}
            {canViewCashFlow && (
              <Route path="cash-flow" element={<CashFlow />} />
            )}
            {canViewCashFlow && (
              <Route path="billable-overages" element={<BillableOverages />} />
            )}
            {canManageTeamMembers && (
              <Route path="team-members" element={<TeamMembers />} />
            )}
            {canViewTranscripts && (
              <Route path="transcripts" element={<Transcripts />} />
            )}
            {canViewTranscripts && (
              <Route path="transcripts/failed" element={<FailedTranscripts />} />
            )}
            <Route path="region-teams" element={<RegionTeamManagement />} />
            {canManageSettings && (
              <Route path="settings" element={<Settings />} />
            )}
            {canManageAccess && (
              <Route path="access-control" element={<AccessControl />} />
            )}
            {canClockSelf && (
              <Route path="clock" element={<ClockPortal />} />
            )}
            {canClockManage && (
              <Route path="clock/manager" element={<ClockManager />} />
            )}
            {canTimesheetTlReview && (
              <Route path="timesheet-review/team-lead" element={<TimesheetReviewTeamLead />} />
            )}
            {canTimesheetOmReview && (
              <Route path="timesheet-review/om" element={<TimesheetReviewOm />} />
            )}
            {canViewPayroll && (
              <Route path="payroll" element={<PayrollPage />} />
            )}
            {canManageSchedule && (
              <Route path="pcc-schedule" element={<PCCSchedule />} />
            )}
          </Route>
        ) : (
          <Route path="*" element={<RedirectToLogin />} />
        )}
      </Routes>
    </>
  );
}

export default App;
